<template>
  <div class="leads-layout">
    <!-- Sidebar -->
    <Sidebar :isAgent="isAgent" />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Top Header -->
      <TopHeader />

      <!-- Page Content -->
      <main class="page-content">
        <div class="leads-view">
          <!-- Page Header -->
          <div class="page-header">
            <div class="header-content">
              <h1 class="page-title">📋 Completed Leads</h1>
              <p class="page-subtitle">View and manage your completed lead submissions</p>
            </div>
            <div class="header-stats">
              <div class="stat-card">
                <div class="stat-number">{{ leads.length }}</div>
                <div class="stat-label">Total Leads</div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="loading-container">
            <div class="loading-spinner"></div>
            <p>Loading completed leads...</p>
          </div>

          <!-- Error State -->
          <div v-else-if="error" class="error-container">
            <div class="error-icon">⚠️</div>
            <h3>Error Loading Leads</h3>
            <p>{{ error }}</p>
            <button @click="loadLeads" class="retry-button">Try Again</button>
          </div>

          <!-- Empty State -->
          <div v-else-if="leads.length === 0" class="empty-state">
            <div class="empty-icon">📋</div>
            <h3>No Completed Leads</h3>
            <p>You don't have any completed leads yet. When customers complete their information, they'll appear here.</p>
          </div>

          <!-- Leads Table -->
          <div v-else class="table-container">
            <div class="table-header">
              <h2 class="table-title">Completed Leads ({{ leads.length }})</h2>
              <div class="table-actions">
                <button @click="loadLeads" class="refresh-button">
                  🔄 Refresh
                </button>
              </div>
            </div>

            <!-- Desktop Table View -->
            <div class="table-wrapper desktop-view">
              <table class="leads-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Phone Number</th>
                    <th>Company Name</th>
                    <th>Business Activity</th>
                    <th>Job Title</th>
                    <th>Email Address</th>
                    <th>Location</th>
                    <th>Age</th>
                    <th>Gender</th>
                    <th>National ID</th>
                    <th>Completed At</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="lead in leads" :key="lead.id" class="lead-row">
                    <td class="name-cell">
                      <div class="name-info">
                        <span class="name">{{ lead.name || '-' }}</span>
                        <span class="lead-id">#{{ lead.id }}</span>
                      </div>
                    </td>
                    <td class="phone-cell">
                      <a :href="`tel:${lead.phone_number}`" class="phone-link">
                        {{ formatPhoneNumber(lead.phone_number) }}
                      </a>
                    </td>
                    <td>{{ lead.company_name || '-' }}</td>
                    <td>{{ lead.business_activity || '-' }}</td>
                    <td>{{ lead.job_title || '-' }}</td>
                    <td>{{ lead.email_address || '-' }}</td>
                    <td>{{ lead.location || lead.address || '-' }}</td>
                    <td>{{ lead.age || '-' }}</td>
                    <td>{{ lead.gender || '-' }}</td>
                    <td>{{ lead.national_id || lead.passport_id || '-' }}</td>
                    <td class="date-cell">
                      <div class="date-info">
                        <span class="date">{{ formatDate(lead.completed_at) }}</span>
                        <span class="time">{{ formatTime(lead.completed_at) }}</span>
                      </div>
                    </td>
                    <td class="actions-cell">
                      <div class="action-buttons">
                        <button @click="viewLead(lead)" class="action-btn view-btn" title="View Details">
                          👁️
                        </button>
                        <button @click="copyLead(lead)" class="action-btn copy-btn" title="Copy Information">
                          📋
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Mobile Cards View -->
            <div class="mobile-view">
              <div v-for="lead in leads" :key="lead.id" class="lead-card">
                <div class="lead-card-header">
                  <div class="lead-name-info">
                    <h3 class="lead-name">{{ lead.name || 'No Name' }}</h3>
                    <span class="lead-id">#{{ lead.id }}</span>
                  </div>
                  <div class="lead-actions">
                    <button @click="viewLead(lead)" class="mobile-action-btn view-btn" title="View Details">
                      👁️
                    </button>
                    <button @click="copyLead(lead)" class="mobile-action-btn copy-btn" title="Copy Information">
                      📋
                    </button>
                  </div>
                </div>

                <div class="lead-card-body">
                  <div class="lead-info-row">
                    <span class="info-label">📞 Phone:</span>
                    <a :href="`tel:${lead.phone_number}`" class="phone-link">
                      {{ formatPhoneNumber(lead.phone_number) }}
                    </a>
                  </div>

                  <div class="lead-info-row" v-if="lead.company_name">
                    <span class="info-label">🏢 Company:</span>
                    <span class="info-value">{{ lead.company_name }}</span>
                  </div>

                  <div class="lead-info-row" v-if="lead.email_address">
                    <span class="info-label">✉️ Email:</span>
                    <span class="info-value">{{ lead.email_address }}</span>
                  </div>

                  <div class="lead-info-row" v-if="lead.location || lead.address">
                    <span class="info-label">📍 Location:</span>
                    <span class="info-value">{{ lead.location || lead.address }}</span>
                  </div>

                  <div class="lead-info-row">
                    <span class="info-label">🕒 Completed:</span>
                    <span class="info-value">{{ formatDate(lead.completed_at) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Lead Details Modal -->
          <div v-if="selectedLead" class="modal-overlay" @click="closeModal">
            <div class="modal-content" @click.stop>
              <div class="modal-header">
                <h3>Lead Details - {{ selectedLead.name }}</h3>
                <button @click="closeModal" class="close-button">✕</button>
              </div>
        <div class="modal-body">
          <div class="lead-details">
            <div class="detail-group">
              <h4>Contact Information</h4>
              <div class="detail-grid">
                <div class="detail-item">
                  <label>Name:</label>
                  <span>{{ selectedLead.name || 'Not provided' }}</span>
                </div>
                <div class="detail-item">
                  <label>Phone Number:</label>
                  <span>{{ formatPhoneNumber(selectedLead.phone_number) }}</span>
                </div>
                <div class="detail-item">
                  <label>Email Address:</label>
                  <span>{{ selectedLead.email_address || 'Not provided' }}</span>
                </div>
                <div class="detail-item">
                  <label>Location/Address:</label>
                  <span>{{ selectedLead.location || selectedLead.address || 'Not provided' }}</span>
                </div>
              </div>
            </div>

            <div class="detail-group">
              <h4>Company Information</h4>
              <div class="detail-grid">
                <div class="detail-item">
                  <label>Company Name:</label>
                  <span>{{ selectedLead.company_name || 'Not provided' }}</span>
                </div>
                <div class="detail-item">
                  <label>Job Title:</label>
                  <span>{{ selectedLead.job_title || 'Not provided' }}</span>
                </div>
                <div class="detail-item">
                  <label>Business Activity:</label>
                  <span>{{ selectedLead.business_activity || 'Not provided' }}</span>
                </div>
              </div>
            </div>

            <div class="detail-group">
              <h4>Personal Information</h4>
              <div class="detail-grid">
                <div class="detail-item">
                  <label>Age:</label>
                  <span>{{ selectedLead.age || 'Not provided' }}</span>
                </div>
                <div class="detail-item">
                  <label>Gender:</label>
                  <span>{{ selectedLead.gender || 'Not provided' }}</span>
                </div>
                <div class="detail-item">
                  <label>National ID/Passport:</label>
                  <span>{{ selectedLead.national_id || selectedLead.passport_id || 'Not provided' }}</span>
                </div>
              </div>
            </div>

            <div class="detail-group">
              <h4>Lead Information</h4>
              <div class="detail-grid">
                <div class="detail-item">
                  <label>Lead ID:</label>
                  <span>#{{ selectedLead.id }}</span>
                </div>
                <div class="detail-item">
                  <label>Created At:</label>
                  <span>{{ formatDateTime(selectedLead.created_at) }}</span>
                </div>
                <div class="detail-item">
                  <label>Completed At:</label>
                  <span>{{ formatDateTime(selectedLead.completed_at) }}</span>
                </div>
                <div class="detail-item">
                  <label>Status:</label>
                  <span class="status-badge complete">✅ Complete</span>
                </div>
              </div>
            </div>
          </div>
              </div>
              <div class="modal-footer">
                <button @click="copyLead(selectedLead)" class="copy-all-btn">📋 Copy All Information</button>
                <button @click="closeModal" class="close-btn">Close</button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import { DashboardService } from '@/services/dashboard'
import Sidebar from '../components/Sidebar.vue'
import TopHeader from '../components/TopHeader.vue'

export default {
  name: 'LeadsView',
  components: {
    Sidebar,
    TopHeader
  },
  data () {
    return {
      isAgent: !!localStorage.getItem('agent_token'),
      leads: [],
      loading: false,
      error: null,
      selectedLead: null
    }
  },
  async mounted () {
    console.log('LeadsView - mounted() called')
    console.log('LeadsView - isAgent:', this.isAgent)
    console.log('LeadsView - auth_token:', localStorage.getItem('auth_token'))
    console.log('LeadsView - agent_token:', localStorage.getItem('agent_token'))
    await this.loadLeads()
  },
  methods: {
    async loadLeads () {
      console.log('LeadsView - loadLeads() called')
      this.loading = true
      this.error = null

      try {
        console.log('LeadsView - About to call DashboardService.getCompletedLeads()')
        const response = await DashboardService.getCompletedLeads()
        console.log('LeadsView - API response:', response)
        if (response.success) {
          this.leads = response.leads || []
        } else {
          this.error = response.message || 'Failed to load leads'
        }
      } catch (error) {
        console.error('Error loading leads:', error)
        this.error = 'Failed to load leads. Please try again.'
      } finally {
        this.loading = false
      }
    },

    formatPhoneNumber (phoneNumber) {
      if (!phoneNumber) return 'Not provided'
      // Format phone number (assuming international format)
      if (phoneNumber.length > 10) {
        return phoneNumber.replace(/(\d{2})(\d{3})(\d{3})(\d{4})/, '+$1 $2 $3 $4')
      }
      return phoneNumber
    },

    formatDate (dateString) {
      if (!dateString) return 'Not provided'
      try {
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        })
      } catch (error) {
        return dateString
      }
    },

    formatTime (dateString) {
      if (!dateString) return ''
      try {
        const date = new Date(dateString)
        return date.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch (error) {
        return ''
      }
    },

    formatDateTime (dateString) {
      if (!dateString) return 'Not provided'
      try {
        const date = new Date(dateString)
        return date.toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch (error) {
        return dateString
      }
    },

    viewLead (lead) {
      this.selectedLead = lead
    },

    closeModal () {
      this.selectedLead = null
    },

    copyLead (lead) {
      const leadInfo = `
Lead Information:
- Name: ${lead.name || 'Not provided'}
- Phone: ${this.formatPhoneNumber(lead.phone_number)}
- Email: ${lead.email_address || 'Not provided'}
- Company: ${lead.company_name || 'Not provided'}
- Job Title: ${lead.job_title || 'Not provided'}
- Business Activity: ${lead.business_activity || 'Not provided'}
- Location: ${lead.location || lead.address || 'Not provided'}
- Age: ${lead.age || 'Not provided'}
- Gender: ${lead.gender || 'Not provided'}
- National ID/Passport: ${lead.national_id || lead.passport_id || 'Not provided'}
- Completed At: ${this.formatDateTime(lead.completed_at)}
      `.trim()

      navigator.clipboard.writeText(leadInfo).then(() => {
        // You could show a success message here
        alert('Lead information copied to clipboard!')
      }).catch(() => {
        alert('Failed to copy to clipboard')
      })
    }
  }
}
</script>

<style scoped>
/* Layout Structure */
.leads-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
  transition: background-color 0.3s ease;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}

.page-content {
  flex: 1;
  padding: 32px;
}

.leads-view {
  background: var(--bg-secondary);
}

/* Page Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
  padding: 32px;
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
}

.header-content h1 {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 8px;
}

.header-content p {
  font-size: 16px;
  color: var(--text-secondary);
  margin: 0;
}

.header-stats {
  display: flex;
  gap: 16px;
}

.stat-card {
  background: var(--bg-secondary);
  padding: 16px 20px;
  border-radius: 12px;
  border: 1px solid var(--border-primary);
  text-align: center;
  min-width: 100px;
}

.stat-number {
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 4px;
}

.stat-label {
  font-size: 12px;
  color: var(--text-secondary);
  font-weight: 500;
}

/* Loading State */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px;
  text-align: center;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--border-primary);
  border-top: 3px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Error State */
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px;
  text-align: center;
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
}

.error-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.error-container h3 {
  color: var(--text-primary);
  margin: 0 0 8px;
}

.error-container p {
  color: var(--text-secondary);
  margin: 0 0 24px;
}

.retry-button {
  background: #667eea;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease;
}

.retry-button:hover {
  background: #5a67d8;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px;
  text-align: center;
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.empty-state h3 {
  color: var(--text-primary);
  margin: 0 0 8px;
}

.empty-state p {
  color: var(--text-secondary);
  margin: 0;
  max-width: 400px;
}

/* Table Container */
.table-container {
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
  overflow: hidden;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid var(--border-primary);
}

.table-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.table-actions {
  display: flex;
  gap: 12px;
}

.refresh-button {
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-primary);
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.refresh-button:hover {
  background: var(--bg-tertiary);
  border-color: var(--border-secondary);
}

/* Table */
.table-wrapper {
  overflow-x: auto;
}

.leads-table {
  width: 100%;
  border-collapse: collapse;
}

.leads-table th {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  font-weight: 600;
  font-size: 14px;
  padding: 16px 12px;
  text-align: left;
  border-bottom: 1px solid var(--border-primary);
  white-space: nowrap;
}

.leads-table td {
  padding: 16px 12px;
  border-bottom: 1px solid var(--border-primary);
  color: var(--text-primary);
  font-size: 14px;
  vertical-align: top;
}

.lead-row:hover {
  background: var(--bg-tertiary);
}

.name-cell .name-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.name {
  font-weight: 600;
  color: var(--text-primary);
}

.lead-id {
  font-size: 12px;
  color: var(--text-secondary);
}

.phone-cell .phone-link {
  color: #667eea;
  text-decoration: none;
  font-weight: 500;
}

.phone-cell .phone-link:hover {
  text-decoration: underline;
}

.date-cell .date-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.date {
  font-weight: 500;
}

.time {
  font-size: 12px;
  color: var(--text-secondary);
}

.actions-cell .action-buttons {
  display: flex;
  gap: 8px;
}

.action-btn {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  padding: 6px 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 12px;
}

.action-btn:hover {
  background: var(--bg-tertiary);
  border-color: var(--border-secondary);
}

.view-btn:hover {
  background: #667eea;
  color: white;
  border-color: #667eea;
}

.copy-btn:hover {
  background: #10b981;
  color: white;
  border-color: #10b981;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid var(--border-primary);
}

.modal-header h3 {
  margin: 0;
  color: var(--text-primary);
  font-size: 18px;
  font-weight: 600;
}

.close-button {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: var(--text-secondary);
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.close-button:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.modal-body {
  padding: 24px;
}

.detail-group {
  margin-bottom: 24px;
}

.detail-group:last-child {
  margin-bottom: 0;
}

.detail-group h4 {
  color: var(--text-primary);
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border-primary);
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-item label {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-item span {
  font-size: 14px;
  color: var(--text-primary);
  font-weight: 500;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.status-badge.complete {
  background: rgba(16, 185, 129, 0.1);
  color: #059669;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px;
  border-top: 1px solid var(--border-primary);
}

.copy-all-btn {
  background: #10b981;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease;
}

.copy-all-btn:hover {
  background: #059669;
}

.close-btn {
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-primary);
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: var(--bg-tertiary);
  border-color: var(--border-secondary);
}

/* Desktop and Mobile View Toggle */
.desktop-view {
  display: block;
}

.mobile-view {
  display: none;
}

/* Mobile Cards */
.lead-card {
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  margin-bottom: 16px;
  overflow: hidden;
  transition: all 0.2s ease;
}

.lead-card:hover {
  border-color: var(--border-secondary);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.lead-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px;
  border-bottom: 1px solid var(--border-primary);
  background: var(--bg-tertiary);
}

.lead-name-info {
  flex: 1;
}

.lead-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 4px;
}

.lead-id {
  font-size: 12px;
  color: var(--text-secondary);
  font-weight: 500;
}

.lead-actions {
  display: flex;
  gap: 8px;
}

.mobile-action-btn {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-action-btn:hover {
  background: var(--bg-tertiary);
  border-color: var(--border-secondary);
}

.mobile-action-btn.view-btn:hover {
  background: #667eea;
  color: white;
  border-color: #667eea;
}

.mobile-action-btn.copy-btn:hover {
  background: #10b981;
  color: white;
  border-color: #10b981;
}

.lead-card-body {
  padding: 16px;
}

.lead-info-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
  padding: 8px 0;
}

.lead-info-row:last-child {
  margin-bottom: 0;
}

.info-label {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-secondary);
  min-width: 80px;
  flex-shrink: 0;
}

.info-value {
  font-size: 14px;
  color: var(--text-primary);
  word-break: break-word;
}

.lead-info-row .phone-link {
  color: #667eea;
  text-decoration: none;
  font-weight: 500;
  font-size: 14px;
}

.lead-info-row .phone-link:hover {
  text-decoration: underline;
}

/* Tablet Responsive */
@media (max-width: 1024px) {
  .page-header {
    flex-direction: column;
    gap: 20px;
    align-items: flex-start;
  }

  .header-stats {
    align-self: flex-end;
  }

  .table-header {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }

  .table-actions {
    align-self: flex-end;
  }
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    padding-top: 70px;
  }

  .page-content {
    padding: 12px;
  }

  .leads-view {
    padding: 0;
  }

  .page-header {
    flex-direction: column;
    gap: 16px;
    padding: 16px;
    margin-bottom: 16px;
  }

  .header-content h1 {
    font-size: 24px;
  }

  .header-content p {
    font-size: 14px;
  }

  .header-stats {
    align-self: stretch;
  }

  .stat-card {
    padding: 12px 16px;
  }

  .stat-number {
    font-size: 20px;
  }

  .table-header {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
    padding: 16px;
  }

  .table-title {
    font-size: 18px;
  }

  .table-actions {
    align-self: stretch;
  }

  .refresh-button {
    width: 100%;
    padding: 12px 16px;
    font-size: 16px;
  }

  /* Hide desktop table, show mobile cards */
  .desktop-view {
    display: none;
  }

  .mobile-view {
    display: block;
    padding: 0 16px 16px;
  }

  .modal-content {
    margin: 10px;
    max-height: calc(100vh - 20px);
  }

  .modal-header {
    padding: 16px;
  }

  .modal-header h3 {
    font-size: 16px;
  }

  .modal-body {
    padding: 16px;
  }

  .modal-footer {
    padding: 16px;
    flex-direction: column;
    gap: 8px;
  }

  .copy-all-btn,
  .close-btn {
    width: 100%;
    padding: 12px;
    font-size: 16px;
  }

  .detail-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .detail-group {
    margin-bottom: 20px;
  }

  .detail-group h4 {
    font-size: 14px;
    margin-bottom: 12px;
  }

  .detail-item label {
    font-size: 11px;
  }

  .detail-item span {
    font-size: 13px;
  }
}

/* Small Mobile Devices */
@media (max-width: 480px) {
  .page-content {
    padding: 8px;
  }

  .page-header {
    padding: 12px;
    margin-bottom: 12px;
  }

  .header-content h1 {
    font-size: 20px;
  }

  .table-header {
    padding: 12px;
  }

  .mobile-view {
    padding: 0 8px 8px;
  }

  .lead-card-header {
    padding: 12px;
  }

  .lead-card-body {
    padding: 12px;
  }

  .lead-name {
    font-size: 15px;
  }

  .info-label {
    font-size: 13px;
    min-width: 70px;
  }

  .info-value {
    font-size: 13px;
  }
}
</style>
